/**
 * UiScriptApi — the `engine.ui` capability module (global service), entity-addressed.
 *
 * The behaviour ↔ UI bridge (W-UI, UI-9): a script gets the screen mounted by the
 * `core.UICanvas` on an entity, feeds it reactive props, and listens to events the
 * .uix root emits (`defineEmits` / `emit('menu', …)`):
 *
 *   const screen = this.engine.ui.getScreen(this.entity);
 *   screen?.setProps({ hp: this.hp, maxHp: 100 });      // shallow-reactive merge
 *   screen?.on('menu', () => this.openMenu());          // .uix root emit('menu')
 *
 * The handle survives canvas remounts (hot-reload) — it is keyed by the GameObject, not the
 * mounted Vue app. Pointer consumption is ADVISORY (design §Input, resolved decision #2):
 * gameplay opts in via `pointerOverUI()`.
 *
 * The concrete backing is injected per runtime (DI): editor/player UI subsystems own the
 * screen bridges; this module stays dependency-free in core. Mirrors VFXScriptApi.
 */

import { scriptApi, scriptFn } from "../scriptApi";
import type { EntityHandle } from "../IEngineContext";

/** A mounted screen, as scripts see it (backed by the UI subsystem's per-canvas bridge). */
export interface UIScreenHandle {
  /** Shallow-merge props into the root .uix component (reactive — the UI updates). */
  setProps(props: Record<string, unknown>): void;
  /** Subscribe to an event the .uix root emits. Returns an unsubscribe function. */
  on(event: string, handler: (...args: unknown[]) => void): () => void;
}

/** The injected backing the ui module resolves against (DI per runtime). */
export interface UIBacking {
  getScreen(entity: EntityHandle): UIScreenHandle | null;
  pointerOverUI(): boolean;
}

@scriptApi("ui", { tier: "safe", version: 1 })
export class UiScriptApi {
  constructor(private readonly _backing: UIBacking) {}

  /**
   * The UI screen of the `core.UICanvas` on `entity` (null when the entity has none).
   * Valid even before the canvas finishes mounting — props apply once it does.
   */
  @scriptFn() getScreen(entity: EntityHandle): UIScreenHandle | null {
    return this._backing.getScreen(entity);
  }

  /**
   * True while the pointer is over interactive/painted UI (or a UI drag is in progress) —
   * gate gameplay pointer actions on it so clicks on the HUD don't fire into the world.
   */
  @scriptFn() pointerOverUI(): boolean {
    return this._backing.pointerOverUI();
  }
}
